// An MFA API app's Policies tab: that app's policies, read through the
// Applications policy adapter. Each row and "New policy" open the shared
// policy pages (/pam/policies/apps/...); "All policies" opens the full list.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Table } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";

import { TableCard, anTableProps, tableEmpty } from "../../design-system/tableUi";
import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { policyActionLabel } from "../../components/policyShell/signals";
import appsAdapter from "../policies/products/apps";
import { policiesPath } from "../policies/policyProducts";
import { appPolicyFields } from "../../service/appPolicyService";

const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };

export default function AppPoliciesTab({ app, active = true }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const appId = app?.id;

  const load = useCallback(() => {
    let live = true;
    setLoading(true);
    setFailed(false);
    appsAdapter
      .list()
      .then((list) => live && setRows(list.filter((r) => appPolicyFields(r).appId === appId)))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("App policies failed", e?.raw ?? e);
        if (live) setFailed(true);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [appId]);
  // Read again each time the tab is shown.
  useEffect(() => (active && appId ? load() : undefined), [active, appId, load]);

  const newPolicy = () => navigate(`${policiesPath("apps", "new")}?app=${encodeURIComponent(appId)}`);
  const detail = (r) => policiesPath("apps", encodeURIComponent(appsAdapter.rowId(r)));

  const columns = useMemo(
    () => [
      {
        title: "Name",
        key: "name",
        render: (_, r) => (
          <Link to={detail(r)} style={{ ...ellipsis, display: "block", fontWeight: 500, color: AN_COLORS.ink }} title={r.policyName}>
            {r.policyName || "Untitled policy"}
          </Link>
        ),
      },
      {
        title: "Directory user",
        key: "user",
        render: (_, r) => {
          const user = appPolicyFields(r).user;
          return (
            <div style={{ minWidth: 0 }}>
              <div style={{ ...ellipsis, fontFamily: AN_MONO, fontSize: "0.8125rem", color: AN_COLORS.ink }} title={user}>
                {user || "Nobody"}
              </div>
              {r.appsView?.userMissing && (
                <div style={{ fontSize: "0.75rem", color: AN_COLORS.warn, marginTop: "0.125rem" }}>Not in the directory: this policy matches nobody</div>
              )}
            </div>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        width: "8rem",
        render: (_, r) => policyActionLabel(appsAdapter.actionOf(r.policyJson || {})),
      },
      {
        title: "Approver",
        key: "approver",
        render: (_, r) => <span style={{ ...ellipsis, display: "block" }}>{appPolicyFields(r).iamUser || ""}</span>,
      },
      {
        title: "On",
        key: "on",
        width: "4.5rem",
        render: (_, r) => (appsAdapter.isActive(r) ? "On" : "Off"),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  let empty = tableEmpty(loading, "No policies yet.").emptyText;
  if (failed) empty = "Couldn't load the policies. Try again.";
  else if (!loading && !rows.length) empty = "No policies yet. Without a policy, users are refused.";

  return (
    <div style={{ display: "grid", gap: "1rem", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.8125rem", color: AN_COLORS.muted, maxWidth: "40rem", lineHeight: 1.5 }}>
          Who may use this app, and who approves the push. Users with no policy are refused.{" "}
          <Link to={policiesPath("apps")}>All policies</Link>
        </span>
        <Button type="primary" icon={<Plus size={14} />} onClick={newPolicy} disabled={!appId}>
          New policy
        </Button>
      </div>
      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={rows}
          rowKey={(r) => appsAdapter.rowId(r)}
          loading={loading}
          tableLayout="fixed"
          locale={{ emptyText: empty }}
          pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        />
      </TableCard>
    </div>
  );
}
